<script setup lang="ts">
import { computed, inject } from 'vue';
import {
  PromotionRequestStatus,
  type CatalogueItem,
  type CatalogueItemDetail,
  type CatalogueItemGroup,
} from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import formatDate from '@/helpers/DateFormatterHelper';
import { getAllCatalogueGroupsFlat } from '@/composables/catalogue/useCatalogueGroupHierarchy';
import formatCost from '@/helpers/CurrencyHelper';
import { useCataloguePermissions } from '@/composables/catalogue/useCataloguePermissions';
import { CatalogueItemActionsKey } from '@/composables/catalogue/useCatalogueItemActions';

const { $gettext } = gettext;
const {
  canEditItem,
  canDuplicateItem,
  canCreateRevision,
  canRequestPromotion,
  canCancelPromotion,
} = useCataloguePermissions();
const actions = inject(CatalogueItemActionsKey)!;

const props = defineProps<{
  item: CatalogueItem;
  groups: CatalogueItemGroup[];
}>();

const canEdit = computed(() => {
  return canEditItem(props.item);
});

const canDelete = computed(() => {
  return canEditItem(props.item);
});

const canDuplicate = computed(() => {
  return canDuplicateItem();
});

const canRevision = computed(() => {
  return canCreateRevision(props.item);
});

const showRequestPromotionButton = computed(() =>
  canRequestPromotion(props.item),
);
const showCancelPromotionButton = computed(() =>
  canCancelPromotion(props.item),
);
const promotionStatus = computed(() => props.item.promotionRequest?.status);
const promotionComment = computed(() => props.item.promotionRequest?.comment);

const groupName = computed(() => {
  // Flatten the hierarchical groups to find the group by UUID
  const flatGroups = getAllCatalogueGroupsFlat(props.groups);
  const group = flatGroups.find((g) => g.uuid === props.item.groupUuid);
  return group?.name ?? '—';
});

const formatBoolean = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return '—';
  }
  return value ? $gettext('Yes') : $gettext('No');
};

const shouldShowField = (value: unknown) => {
  return value !== null && value !== undefined && value !== '';
};
</script>

<template>
  <div>
    <div class="d-flex justify-end">
      <v-btn-group variant="outlined" density="comfortable">
        <v-btn
          v-if="canEdit"
          prepend-icon="mdi-pencil"
          @click="actions.editItem(props.item)"
        >
          {{ $gettext('Edit') }}
        </v-btn>
        <v-menu>
          <template v-slot:activator="{ props: menuProps }">
            <v-btn v-bind="menuProps" icon="mdi-dots-vertical" />
          </template>
          <v-list>
            <v-list-item
              v-if="canDuplicate"
              @click="actions.duplicateItem(props.item)"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-content-copy" size="small" />
              </template>
              <v-list-item-title>{{ $gettext('Duplicate') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canRevision"
              @click="actions.createRevision(props.item)"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-source-branch-plus" size="small" />
              </template>
              <v-list-item-title>{{
                $gettext('Create Revision')
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="showRequestPromotionButton"
              @click="
                actions.requestPromotion(props.item as CatalogueItemDetail)
              "
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-arrow-up-circle" size="small" />
              </template>
              <v-list-item-title>{{
                $gettext('Request Promotion')
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="showCancelPromotionButton"
              @click="
                actions.cancelPromotion(props.item as CatalogueItemDetail)
              "
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-cancel" size="small" color="error" />
              </template>
              <v-list-item-title class="text-error">{{
                $gettext('Cancel Promotion Request')
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="canDelete"
              @click="actions.deleteItem(props.item)"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-delete" size="small" color="error" />
              </template>
              <v-list-item-title class="text-error">{{
                $gettext('Delete')
              }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-btn-group>
    </div>

    <!-- Promotion Status -->
    <v-alert
      v-if="promotionStatus === PromotionRequestStatus.PENDING"
      type="info"
      variant="tonal"
      class="mt-4"
      density="compact"
    >
      <div class="d-flex align-center">
        {{ $gettext('Pending Standard Promotion') }} -
        {{ $gettext('Waiting for review.') }}
      </div>
    </v-alert>
    <v-alert
      v-else-if="promotionStatus === PromotionRequestStatus.REJECTED"
      type="warning"
      variant="tonal"
      class="mt-4"
      density="compact"
    >
      <div>
        <div class="d-flex align-center">
          {{ $gettext('Promotion Request Rejected') }}
        </div>
        <div v-if="promotionComment" class="mt-2 text-body-2">
          <strong>{{ $gettext('Reason') }}:</strong> {{ promotionComment }}
        </div>
      </div>
    </v-alert>

    <div class="pa-4">
      <v-row>
        <v-col cols="12" md="6">
          <v-list-item>
            <template v-slot:prepend>
              <v-icon
                :icon="
                  item.isAssembly ? 'mdi-package-variant' : 'mdi-cube-outline'
                "
              />
            </template>
            <v-list-item-title>{{ $gettext('Type') }}</v-list-item-title>
            <v-list-item-subtitle>{{
              item.isAssembly ? $gettext('Assembly') : $gettext('Item')
            }}</v-list-item-subtitle>
          </v-list-item>
        </v-col>
        <v-col cols="12" md="6">
          <v-list-item v-if="shouldShowField(item.category)">
            <template v-slot:prepend>
              <v-icon icon="mdi-tag" />
            </template>
            <v-list-item-title>{{ $gettext('Category') }}</v-list-item-title>
            <v-list-item-subtitle>{{ item.category }}</v-list-item-subtitle>
          </v-list-item>
        </v-col>
      </v-row>
      <v-row v-if="shouldShowField(item.version)">
        <v-col cols="12" md="6">
          <v-list-item>
            <template v-slot:prepend>
              <v-icon icon="mdi-source-branch" />
            </template>
            <v-list-item-title>{{ $gettext('Version') }}</v-list-item-title>
            <v-list-item-subtitle>v{{ item.version }}</v-list-item-subtitle>
          </v-list-item>
        </v-col>
        <v-col cols="12" md="6">
          <div class="d-flex ga-2 align-center" style="min-height: 48px">
            <v-btn
              v-if="item.previousVersionUuid"
              prepend-icon="mdi-arrow-left-circle"
              variant="outlined"
              size="small"
              @click="actions.openChild(item.previousVersionUuid!)"
            >
              {{ $gettext('Previous Version') }}
            </v-btn>
            <v-btn
              v-if="item.nextVersionUuid"
              prepend-icon="mdi-arrow-right-circle"
              variant="outlined"
              size="small"
              @click="actions.openChild(item.nextVersionUuid!)"
            >
              {{ $gettext('Next Version') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-list-item v-if="shouldShowField(item.cost)">
            <template v-slot:prepend>
              <v-icon icon="mdi-cash" />
            </template>
            <v-list-item-title>{{ $gettext('Cost') }}</v-list-item-title>
            <v-list-item-subtitle>{{
              formatCost(item.cost, item.currency)
            }}</v-list-item-subtitle>
          </v-list-item>
        </v-col>
        <v-col cols="12" md="6">
          <v-list-item v-if="shouldShowField(item.groupUuid)">
            <template v-slot:prepend>
              <v-icon icon="mdi-folder-outline" />
            </template>
            <v-list-item-title>{{ $gettext('Group') }}</v-list-item-title>
            <v-list-item-subtitle>{{ groupName }}</v-list-item-subtitle>
          </v-list-item>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-list-item v-if="shouldShowField(item.createdAt)">
            <template v-slot:prepend>
              <v-icon icon="mdi-calendar" />
            </template>
            <v-list-item-title>{{ $gettext('Created') }}</v-list-item-title>
            <v-list-item-subtitle>{{
              formatDate(item.createdAt, true)
            }}</v-list-item-subtitle>
          </v-list-item>
        </v-col>
        <v-col cols="12" md="6">
          <v-list-item v-if="shouldShowField(item.erpId)">
            <template v-slot:prepend>
              <v-icon icon="mdi-numeric" />
            </template>
            <v-list-item-title>{{ $gettext('Xentral ID') }}</v-list-item-title>
            <v-list-item-subtitle>{{ item.erpId }}</v-list-item-subtitle>
          </v-list-item>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-list-item v-if="shouldShowField(item.drawingNumber)">
            <template v-slot:prepend>
              <v-icon icon="mdi-pencil-ruler" />
            </template>
            <v-list-item-title>{{
              $gettext('Drawing Number')
            }}</v-list-item-title>
            <v-list-item-subtitle>{{
              item.drawingNumber
            }}</v-list-item-subtitle>
          </v-list-item>
        </v-col>
      </v-row>

      <div v-if="shouldShowField(item.description)" class="mt-4">
        <h3 class="text-h6 mb-2">{{ $gettext('Description') }}</h3>
        <p>{{ item.description }}</p>
      </div>

      <v-divider class="my-4" />
      <h3 class="text-h6 my-4">{{ $gettext('Technical Specification') }}</h3>

      <!-- Technical specifications in a single column layout for better balance -->
      <div class="technical-specs">
        <v-list-item v-if="shouldShowField(item.activationPrinciple)">
          <v-list-item-title
            >{{ $gettext('Activation Principle') }}:</v-list-item-title
          >
          <v-list-item-subtitle>{{
            item.activationPrinciple
          }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.materialType)">
          <v-list-item-title
            >{{ $gettext('Material Type') }}:</v-list-item-title
          >
          <v-list-item-subtitle>{{ item.materialType }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.gripControlSensor)">
          <v-list-item-title
            >{{ $gettext('Grip Control Sensor') }}:</v-list-item-title
          >
          <v-list-item-subtitle>{{
            formatBoolean(item.gripControlSensor)
          }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.distanceSensor)">
          <v-list-item-title
            >{{ $gettext('Distance Sensor') }}:</v-list-item-title
          >
          <v-list-item-subtitle>{{
            formatBoolean(item.distanceSensor)
          }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.airSupplyNeeded)">
          <v-list-item-title
            >{{ $gettext('Air Supply Needed') }}:</v-list-item-title
          >
          <v-list-item-subtitle>{{
            formatBoolean(item.airSupplyNeeded)
          }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.vacuumSupplyNeeded)">
          <v-list-item-title
            >{{ $gettext('Vacuum Supply Needed') }}:</v-list-item-title
          >
          <v-list-item-subtitle>{{
            formatBoolean(item.vacuumSupplyNeeded)
          }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.weightG)">
          <v-list-item-title>{{ $gettext('Weight') }}:</v-list-item-title>
          <v-list-item-subtitle>{{ item.weightG }} g</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.strokeMm)">
          <v-list-item-title>{{ $gettext('Stroke') }}:</v-list-item-title>
          <v-list-item-subtitle>{{ item.strokeMm }} mm</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.maxGripForceN)">
          <v-list-item-title
            >{{ $gettext('Max Grip Force') }}:</v-list-item-title
          >
          <v-list-item-subtitle
            >{{ item.maxGripForceN }} N</v-list-item-subtitle
          >
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.ipClass)">
          <v-list-item-title>{{ $gettext('IP Class') }}:</v-list-item-title>
          <v-list-item-subtitle>{{ item.ipClass }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.payloadKg)">
          <v-list-item-title>{{ $gettext('Payload') }}:</v-list-item-title>
          <v-list-item-subtitle>{{ item.payloadKg }} kg</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.reachMm)">
          <v-list-item-title>{{ $gettext('Reach') }}:</v-list-item-title>
          <v-list-item-subtitle>{{ item.reachMm }} mm</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.dof)">
          <v-list-item-title
            >{{ $gettext('Degrees of Freedom') }}:</v-list-item-title
          >
          <v-list-item-subtitle>{{ item.dof }}</v-list-item-subtitle>
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.pneumaticTubeDiameterMm)">
          <v-list-item-title
            >{{ $gettext('Pneumatic Tube Diameter') }}:</v-list-item-title
          >
          <v-list-item-subtitle
            >{{ item.pneumaticTubeDiameterMm }} mm</v-list-item-subtitle
          >
        </v-list-item>
        <v-list-item v-if="shouldShowField(item.robotVersion)">
          <v-list-item-title
            >{{ $gettext('Robot Version') }}:</v-list-item-title
          >
          <v-list-item-subtitle>{{ item.robotVersion }}</v-list-item-subtitle>
        </v-list-item>
      </div>

      <div
        v-if="item.customFields && Object.keys(item.customFields).length"
        class="technical-specs mt-4"
      >
        <h4 class="text-subtitle-1 font-weight-medium mb-2">
          {{ $gettext('Custom Fields') }}
        </h4>
        <v-list-item v-for="(field, key) in item.customFields" :key="key">
          <template v-slot:prepend>
            <v-icon
              :icon="
                field.type === 'string'
                  ? 'mdi-format-text'
                  : field.type === 'integer'
                    ? 'mdi-numeric'
                    : 'mdi-decimal'
              "
              size="small"
            />
          </template>
          <v-list-item-title>{{ key }}:</v-list-item-title>
          <v-list-item-subtitle>{{ field.value }}</v-list-item-subtitle>
        </v-list-item>
      </div>
    </div>
  </div>
</template>

<style scoped>
.v-card {
  width: 100%;
}

.technical-specs {
  max-width: 600px;
}

.technical-specs .v-list-item {
  padding: 8px 0;
  min-height: auto;
}

.technical-specs .v-list-item-title {
  font-weight: 500;
  margin-bottom: 4px;
}

.technical-specs .v-list-item-subtitle {
  color: rgba(0, 0, 0, 0.7);
}

.v-list-item {
  padding: 6px 0;
  min-height: auto;
}

.v-list-item-title {
  font-weight: 500;
  margin-bottom: 4px;
}

.v-list-item-subtitle {
  color: rgba(0, 0, 0, 0.7);
}
</style>
